import React from 'react';
import { Container, Grid } from '@mui/material';
import { createGroupInvitation, deleteGroupInvitation, updateGroupInvitation } from '../../api-admin/group-invitations';
import { GroupInvitationForListType, GroupInvitationType } from '../../types';
import toast from '../../utils/toast';
import { InfoText, Popup } from '../CommonComponents';
import {
    AutocompleteField,
    BoolField,
    EmailField,
    PhoneField,
    PopupForm
} from '../CommonComponents/Form';
import { searchTenants } from '../../api-admin/tenants';

type PropsType = {
    invitations: GroupInvitationForListType[],
    invitation: GroupInvitationType | null,
    setInvitation: (gi: Partial<GroupInvitationType> | null, options?: { reset: boolean }) => void
    refetch: () => void
};

export default function GroupInvitationForm(props: PropsType) {
    const {
        invitations,
        invitation,
        setInvitation,
        refetch
    } = props;

    const [pickedTenant, setPickedTenant] = React.useState<{
        id: number,
        userCount: number
    } | null>(null);

    const [showWatchlistDeletionInfo, setShowWatchlistDeletionInfo] = React.useState(false);
    const [originalIsAccepted, setOriginalIsAccepted] = React.useState<boolean | undefined>(undefined);

    React.useEffect(() => {
        if (invitation?.isAccepted === undefined) {
            setOriginalIsAccepted(undefined);
        } else if (originalIsAccepted === undefined) {
            setOriginalIsAccepted(invitation?.isAccepted);
        }
    }, [invitation?.isAccepted, originalIsAccepted]);

    const { isValidTenant, isRegisteredTenant, tenantStatusText } = React.useMemo(() => {
        if (invitation?.id) {
            return ({
                isValidTenant: true,
                isRegisteredTenant: null,
                tenantStatusText: ''
            }); // Only if edit
        }
        if (!invitation?.tenantId) {
            return ({
                isValidTenant: false,
                isRegisteredTenant: null,
                tenantStatusText: 'Välj ett företag att bjudan in.'
            });
        }
        const tenantInvitation = invitations.find((i) => i.tenantId === invitation.tenantId);
        if (tenantInvitation) {
            return ({
                isValidTenant: false,
                isRegisteredTenant: null,
                tenantStatusText: 'Det finns redan en gruppinbjudan för företaget, redigera den istället.'
            });
        }
        const tenant = pickedTenant && Number(pickedTenant.id) === Number(invitation.tenantId)
            ? pickedTenant
            : null;
        if (tenant?.userCount) {
            return ({
                isValidTenant: true,
                isRegisteredTenant: true,
                // eslint-disable-next-line max-len
                tenantStatusText: 'Företaget är registrerat, välj om du vill skicka notiser till befintliga användare eller extern inbjudan via e-post.'
            });
        }
        return ({
            isValidTenant: true,
            isRegisteredTenant: false,
            // eslint-disable-next-line max-len
            tenantStatusText: 'Företaget är inte registrerat, fyll i e-postadress för att skicka en extern inbjudan.'
        });
    }, [invitation?.id, invitation?.tenantId, invitations, pickedTenant]);

    const submit = React.useCallback(async () => {
        if (invitation) {
            const isAcceptedHasChanged = invitation.isAccepted !== originalIsAccepted;
            try {
                if (invitation.id) {
                    await updateGroupInvitation(invitation.id, invitation);
                    if (isAcceptedHasChanged && !invitation.isAccepted) {
                        setShowWatchlistDeletionInfo(true);
                    }
                    toast('Gruppinbjudan uppdaterad');
                } else {
                    await createGroupInvitation(invitation);
                    toast('Gruppinbjudan skapad');
                }
                refetch();
                setInvitation(null, { reset: true });
            } catch (e) {
                toast(e, `Kunde inte ${invitation.id ? 'redigera' : 'skapa'} gruppinbjudan, försök igen`);
            }
        }
    }, [invitation, originalIsAccepted, refetch, setInvitation]);

    const remove = React.useCallback(async () => {
        if (invitation?.id) {
            try {
                await deleteGroupInvitation(invitation.id);
                toast('Gruppinbjudan borttagen');
                if (invitation.isAccepted) {
                    setShowWatchlistDeletionInfo(true);
                }
                refetch();
                setInvitation(null, { reset: true });
            } catch (e) {
                toast(e, 'Kunde inte ta bort gruppinbjudan, försök igen');
            }
        }
    }, [invitation?.id, invitation?.isAccepted, refetch, setInvitation]);

    const cancel = React.useCallback(() => {
        setInvitation(null, { reset: true });
    }, [setInvitation]);

    return (
        <Container>
            <PopupForm
                open={!!invitation}
                title={invitation?.id ? 'Redigera gruppinbjudan' : 'Skapa gruppinbjudan'}
                okLabel={invitation?.id ? 'Spara' : 'Skapa'}
                handleOk={submit}
                handleDelete={invitation?.id ? remove : undefined}
                handleClose={cancel}
                okDisabled={!isValidTenant || (!invitation?.id && isRegisteredTenant === false && !invitation?.invitedEmail)}
            >
                <Grid container spacing={2} alignContent="space-between">
                    <Grid item xs={12}>
                        <AutocompleteField<{
                            id: number | string,
                            name: string,
                            orgNumber?: string,
                            userCount?: number
                        }>
                            label="Företag"
                            optionIdKey="id"
                            defaultOption={!invitation?.tenantId ? null : {
                                id: invitation?.tenantId,
                                name: invitation?.tenantName || ''
                            }}
                            fetchOptions={(query) => searchTenants(query || '')}
                            minQueryLength={3}
                            getOptionName={(o) => o.name}
                            getOptionDescription={(o) => o.orgNumber || ''}
                            onChange={(tenant) => {
                                setPickedTenant(tenant ? {
                                    id: Number(tenant.id),
                                    userCount: tenant.userCount || 0
                                } : null);
                                setInvitation({
                                    tenantId: tenant === null ? null : tenant.id,
                                    tenantName: tenant === null ? null : tenant.name
                                });
                            }}
                            disabled={!!invitation?.id}
                            required
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <BoolField
                            name="isAccepted"
                            value={invitation?.isAccepted || false}
                            label="Accepterat"
                            onChange={setInvitation}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <BoolField
                            name="canPublish"
                            value={invitation?.canPublish || false}
                            label="Kan publicera"
                            onChange={setInvitation}
                        />
                    </Grid>

                    {!invitation?.id && (
                        <Grid item xs={12}>
                            <InfoText>
                                {tenantStatusText}
                            </InfoText>
                        </Grid>
                    )}

                    {!invitation?.id && isValidTenant && (
                        <>
                            {isRegisteredTenant && (
                                <Grid item xs={12}>
                                    <BoolField
                                        name="sendNotifications"
                                        value={invitation?.sendNotifications || false}
                                        label="Skicka notiser till befintliga användare"
                                        onChange={setInvitation}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <EmailField
                                    name="invitedEmail"
                                    label="E-postadress för extern inbjudan"
                                    value={invitation?.invitedEmail || ''}
                                    required={isRegisteredTenant === false}
                                    onChange={setInvitation}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <PhoneField
                                    name="invitedPhone"
                                    label="Mobilnummer"
                                    helperText="Sparas som kontaktuppgift, inget SMS skickas."
                                    value={invitation?.invitedPhone || ''}
                                    onChange={setInvitation}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </PopupForm>
            <Popup
                open={showWatchlistDeletionInfo}
                title="Borttagna bevakningar"
                body="Samtliga företagets bevakningar knutna till gruppen har tagit bort."
                okLabel="Ok"
                handleOk={() => setShowWatchlistDeletionInfo(false)}
            />
        </Container>
    );
}
